/**
 * Pure variable-reordering logic for the CRF Builder's drag-and-drop.
 *
 * A form's variables live in a flat `variables[]` array (the rendered order),
 * and (when the form has structure) `sections[].variableIds` records which
 * section each variable belongs to. The renderer draws a section header
 * whenever the section changes between consecutive flat variables, which means
 * the flat order MUST stay section-contiguous (all of a section's variables in
 * one run). The save path buckets the flat list by section membership, so a
 * reorder only persists correctly if membership and flat order agree.
 *
 * `moveVariableBefore` moves one variable to sit immediately before another
 * (cross-section allowed), returning a new flat order AND rebuilt section
 * membership that together preserve those invariants. Kept React-free so it's
 * unit-testable (web has no test runner; exercised via the functions vitest).
 */
export interface ReorderableVar {
  id: string;
}
export interface ReorderableSection {
  variableIds: string[];
}

export interface ReorderResult<V, S> {
  variables: V[];
  sections: S[] | undefined;
}

/**
 * Move `draggedId` so it sits immediately before `beforeId` in the flat list
 * (`beforeId === null` ⇒ append to the end). When the form has sections, the
 * dragged variable JOINS the section of the variable it lands in front of (or
 * the last section when appended), and every section's `variableIds` is rebuilt
 * from the new flat order so membership, headers, and persistence stay in sync.
 */
export function moveVariableBefore<
  V extends ReorderableVar,
  S extends ReorderableSection,
>(
  variables: V[],
  sections: S[] | undefined,
  draggedId: string,
  beforeId: string | null,
): ReorderResult<V, S> {
  if (draggedId === beforeId) return { variables, sections };
  const dragged = variables.find((v) => v.id === draggedId);
  if (!dragged) return { variables, sections };

  // 1. New flat order — pull the dragged var out, re-insert before `beforeId`.
  const without = variables.filter((v) => v.id !== draggedId);
  let insertAt = beforeId == null ? without.length : without.findIndex((v) => v.id === beforeId);
  if (insertAt < 0) insertAt = without.length; // beforeId gone ⇒ append
  const newVariables = [...without.slice(0, insertAt), dragged, ...without.slice(insertAt)];

  if (!sections || sections.length === 0) {
    return { variables: newVariables, sections };
  }

  // 2. Decide the dragged var's new section: the section of its following
  //    neighbour (so "insert before X" ⇒ join X's section, which makes the top
  //    of any section reachable), falling back to the preceding neighbour, then
  //    to its own current section.
  const idToSection = new Map<string, number>();
  sections.forEach((s, i) => s.variableIds.forEach((id) => idToSection.set(id, i)));
  const pos = newVariables.findIndex((v) => v.id === draggedId);
  const followingId = pos + 1 < newVariables.length ? newVariables[pos + 1].id : null;
  const precedingId = pos - 1 >= 0 ? newVariables[pos - 1].id : null;
  let newSec: number | undefined =
    followingId != null ? idToSection.get(followingId) : undefined;
  if (newSec === undefined && precedingId != null) newSec = idToSection.get(precedingId);
  if (newSec === undefined) newSec = idToSection.get(draggedId);

  // 3. Rebuild each section's variableIds from the new flat order, using the
  //    existing membership for every variable except the dragged one.
  const memberOf = (id: string): number | undefined =>
    id === draggedId ? newSec : idToSection.get(id);
  const newSections = sections.map((s, i) => ({
    ...s,
    variableIds: newVariables.filter((v) => memberOf(v.id) === i).map((v) => v.id),
  }));

  return { variables: newVariables, sections: newSections };
}
